"use client";

import { redact, REDACTION_VERSION } from "./redactor";
import {
  CORRELATION_HEADER,
  generateCorrelationId,
  validateCorrelationId,
} from "./correlation";
import type { Level, LogInput } from "./log";

const SESSION_CORR_KEY = "blaundry_correlation_id";
const SESSION_SESSION_KEY = "blaundry_session_id";

function getCorrelationId(): string {
  if (typeof window === "undefined") return generateCorrelationId();
  try {
    const id = validateCorrelationId(window.sessionStorage.getItem(SESSION_CORR_KEY));
    window.sessionStorage.setItem(SESSION_CORR_KEY, id);
    return id;
  } catch { return generateCorrelationId(); }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.sessionStorage.getItem(SESSION_SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_SESSION_KEY, id);
    }
    return id;
  } catch { return crypto.randomUUID(); }
}

type ClientLogInput = Omit<LogInput, "source"> & { level: Level };

async function emit(input: ClientLogInput): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const body = {
      ...input,
      error: undefined,
      error_name: input.error instanceof Error ? input.error.name : undefined,
      error_stack: input.error instanceof Error ? input.error.stack : undefined,
      message: input.message ?? (input.error instanceof Error ? input.error.message : undefined),
      source: "client" as const,
      correlation_id: input.correlation_id ?? getCorrelationId(),
      session_id: input.session_id ?? getSessionId(),
      user_agent: navigator.userAgent,
      payload: input.payload ? (redact(input.payload) as Record<string, unknown>) : null,
      occurred_at: new Date().toISOString(),
      redaction_version: REDACTION_VERSION,
    };
    let encoded = JSON.stringify(redact(body));
    if (new TextEncoder().encode(encoded).byteLength > 32768) {
      // Keep a valid event when a large payload would exceed the intake cap.
      encoded = JSON.stringify(redact({...body,payload:{truncated:true}}));
    }
    await fetch("/api/observe/ingest", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [CORRELATION_HEADER]: body.correlation_id,
      },
      body: encoded,
      keepalive: true,
    });
  } catch {
    // never throw from logger
  }
}

export const clientLog = {
  debug: (i: Omit<ClientLogInput, "level">) => emit({ ...i, level: "debug" }),
  info: (i: Omit<ClientLogInput, "level">) => emit({ ...i, level: "info" }),
  warn: (i: Omit<ClientLogInput, "level">) => emit({ ...i, level: "warn" }),
  error: (i: Omit<ClientLogInput, "level">) => emit({ ...i, level: "error" }),
  fatal: (i: Omit<ClientLogInput, "level">) => emit({ ...i, level: "fatal" }),
};

let installed = false;

export function installClientCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    void clientLog.error({
      kind: "window_error",
      message: event.message,
      error: event.error ?? new Error(event.message),
      route: window.location.pathname,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    void clientLog.error({
      kind: "unhandled_rejection",
      message: reason instanceof Error ? reason.message : String(reason),
      error: reason instanceof Error ? reason : new Error(String(reason)),
      route: window.location.pathname,
    });
  });
}

/**
 * Drop-in replacement for fetch() that injects x-correlation-id, measures
 * duration, and logs non-2xx responses. Use for internal API calls; external
 * fetches stay raw.
 */
export async function trackedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const corr = getCorrelationId();
  const headers = new Headers(init.headers);
  if (!headers.has(CORRELATION_HEADER)) headers.set(CORRELATION_HEADER, corr);

  const startedAt = Date.now();
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

  try {
    const res = await fetch(input, { ...init, headers });
    if (!res.ok) {
      void clientLog.warn({
        kind: "fetch_non_ok",
        status_code: res.status,
        route: url,
        duration_ms: Date.now() - startedAt,
        correlation_id: corr,
      });
    }
    return res;
  } catch (err) {
    void clientLog.error({
      kind: "fetch_failed",
      route: url,
      error: err,
      duration_ms: Date.now() - startedAt,
      correlation_id: corr,
    });
    throw err;
  }
}
